// Matches Commerce payment-utils, without a default currency for financial writes.
export function currencyDigits(currency) {
  if (!/^[A-Z]{3}$/.test(currency || '')) throw new Error('Missing or invalid currency')
  return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits
}

export function majorToMinor(value, currency) {
  const normalized = String(value ?? '').trim()
  if (!/^(?:0|[1-9]\d*)(?:\.\d+)?$/.test(normalized)) throw new Error('Enter a valid amount / 请输入有效金额')
  const digits = currencyDigits(currency)
  const [whole, fraction = ''] = normalized.split('.')
  if (fraction.length > digits && /[1-9]/.test(fraction.slice(digits))) throw new Error(`Invalid precision for ${currency} / 金额精度不正确`)
  const amount = BigInt(whole) * 10n ** BigInt(digits) + BigInt(fraction.slice(0, digits).padEnd(digits, '0') || '0')
  if (amount > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error('Amount is too large / 金额过大')
  return Number(amount)
}

export function minorToMajor(value, currency) {
  const digits = currencyDigits(currency)
  return (Number(value) / 10 ** digits).toFixed(digits)
}

const EMPTY_PRODUCT = { id: '', sku: '', name: '', description: '', unitAmount: '0', currency: 'USD', kind: 'physical', status: 'inactive' }
export function productForm(product) {
  return Object.fromEntries(Object.entries(EMPTY_PRODUCT).map(([key, fallback]) => [key, key === 'unitAmount' ? String(product?.[key] ?? fallback) : product?.[key] ?? fallback]))
}

export function productPayload(form) {
  const payload = productForm(form)
  for (const field of ['id', 'sku', 'name', 'description', 'currency']) payload[field] = String(payload[field]).trim()
  if (!/^[A-Za-z0-9][A-Za-z0-9._-]{0,127}$/.test(payload.id)) throw new Error('Invalid product ID / 商品 ID 不正确')
  for (const [field, max] of [['sku', 128], ['name', 200], ['description', 4000]]) {
    if ((field !== 'description' && !payload[field]) || payload[field].length > max) throw new Error(`Invalid ${field} / 商品字段不正确`)
  }
  if (!/^\d+$/.test(payload.unitAmount)) throw new Error('Price must be a non-negative integer in minor units / 价格必须为最小货币单位的非负整数')
  payload.unitAmount = Number(payload.unitAmount)
  if (!Number.isSafeInteger(payload.unitAmount)) throw new Error('Price is too large / 价格过大')
  currencyDigits(payload.currency)
  if (!['physical', 'digital'].includes(payload.kind) || !['active', 'inactive'].includes(payload.status)) throw new Error('Invalid product kind or status')
  return payload
}

export function refundableAmount(aggregate) {
  const order = aggregate?.order || aggregate
  const paid = order?.amounts?.paid
  const refunded = order?.amounts?.refunded
  if (!Number.isSafeInteger(paid) || !Number.isSafeInteger(refunded) || paid < 0 || refunded < 0) return 0
  const pending = (aggregate?.refunds || []).filter((refund) => ['pending', 'processing'].includes(refund.status))
  if (pending.some((refund) => !Number.isSafeInteger(refund.amount) || refund.amount < 0)) return 0
  const available = paid - refunded - pending.reduce((sum, refund) => sum + refund.amount, 0)
  return Number.isSafeInteger(available) ? Math.max(0, available) : 0
}

export function refundPayload(aggregate, amountText, reason) {
  const order = aggregate?.order || aggregate
  const amount = majorToMinor(amountText, order?.currency)
  const original = originalRefundPayment(aggregate)
  if (!order?.id || !original) throw new Error('Missing or ambiguous successful charge / 缺少成功交易或原支付渠道不明确')
  if (amount <= 0 || amount > refundableAmount(aggregate)) throw new Error('Amount must be positive and no greater than the refundable balance / 金额须大于零且不超过可退款余额')
  if (!['customer_request', 'duplicate', 'fraud'].includes(reason)) throw new Error('Invalid refund reason')
  return { ...original, amount, reason }
}

// One key per exact operation, including after an ambiguous network failure.
export function operationKey(cache, path, method, body) {
  const fingerprint = JSON.stringify([path, method, body])
  if (!cache.has(fingerprint)) cache.set(fingerprint, crypto.randomUUID())
  return cache.get(fingerprint)
}

// Detail aggregates, not enriched list rows, are the authority. The upstream
// refund service needs a succeeded charge; an intent alone cannot be refunded.
export function originalRefundPayment(aggregate) {
  const charges = (aggregate?.paymentTransactions || []).filter((item) => item.kind === 'charge' && item.status === 'succeeded')
  if (charges.length !== 1) return null
  const charge = charges[0]
  if (!charge.id || !charge.provider) return null
  const order = aggregate?.order
  if (charge.currency && charge.currency !== order?.currency) return null
  const intents = (aggregate?.paymentIntents || []).filter((item) => item.status === 'succeeded')
  if (intents.some((item) => !item.provider || item.provider !== charge.provider || (item.currency && item.currency !== order?.currency))) return null
  return { provider: charge.provider, paymentTransactionId: charge.id }
}

function refundLedger(aggregate) {
  return JSON.stringify([aggregate?.order?.amounts, (aggregate?.refunds || []).map(({ id, status, amount }) => ({ id, status, amount }))])
}

export function pendingRefundOperation(cache, aggregate) {
  const operation = cache.get(`refund-order:${aggregate?.order?.id}`)
  if (operation?.state === 'succeeded' && operation.ledger !== refundLedger(aggregate)) return null
  return operation || null
}

export function beginRefundOperation(cache, aggregate, body) {
  const pending = pendingRefundOperation(cache, aggregate)
  if (pending) {
    if (pending.state === 'succeeded') throw new Error('Refund submitted; refresh the authoritative refund records before another request')
    if (JSON.stringify(pending.body) !== JSON.stringify(body)) throw new Error('Refund outcome unknown; only the original amount and reason may be retried')
    return pending
  }
  const operation = { key: crypto.randomUUID(), body: { ...body }, ledger: refundLedger(aggregate), state: 'pending' }
  cache.set(`refund-order:${aggregate.order.id}`, operation)
  return operation
}

export function settleRefundOperation(cache, aggregate, operation, error) {
  if (!error) { operation.state = 'succeeded'; return }
  // 409 may mean an in-progress/idempotency conflict; 5xx and transport failures
  // cannot prove that the provider did not execute the refund.
  const definiteRejection = [400, 401, 403, 404, 422].includes(error.status) && error.code !== 'invalid_response'
  if (definiteRejection) cache.delete(`refund-order:${aggregate.order.id}`)
  else operation.state = 'unknown'
}
